/* eslint-disable react/jsx-no-constructed-context-values */
import React, { createContext, useEffect, useState } from 'react';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import { USER_SETTINGS_STORAGE_KEY } from '../constants/Soter';
import i18n from '../locales';
import { DEFAULT_LOCALE, resolveSupportedLocale } from '../locales/localeConfig';

const defaultSettings = {
	locale: DEFAULT_LOCALE,
};

const normalizeSettings = (settings = {}) => {
	const locale = resolveSupportedLocale(settings.locale || settings.language);
	const normalizedSettings = merge({}, settings, { locale });

	if (Object.prototype.hasOwnProperty.call(normalizedSettings, 'language')) {
		delete normalizedSettings.language;
	}

	return normalizedSettings;
};

export const restoreSettings = () => {
	let settings = null;

	try {
		const storedData = window.localStorage.getItem(USER_SETTINGS_STORAGE_KEY);
		if (storedData) {
			settings = normalizeSettings(JSON.parse(storedData));
		}
	} catch (err) {
		console.error(err);
	}
	return settings;
};

export const storeSettings = (settings) => {
	window.localStorage.setItem(
		USER_SETTINGS_STORAGE_KEY,
		JSON.stringify(settings)
	);
};

const GlobalConfigContext = createContext({
	settings: defaultSettings,
	saveSettings: () => {},
});

export function GlobalProvider({ settings, children }) {
	const [currentSettings, setCurrentSettings] = useState(
		normalizeSettings(settings || defaultSettings)
	);

	const handleSaveSettings = (update = {}) => {
		const mergedSettings = normalizeSettings(
			merge({}, currentSettings, update)
		);

		setCurrentSettings(mergedSettings);
		storeSettings(mergedSettings);
	};

	useEffect(() => {
		const restoredSettings = restoreSettings();
		if (restoredSettings) {
			setCurrentSettings(normalizeSettings(restoredSettings));
		}
	}, []);

	useEffect(() => {
		i18n.changeLanguage(resolveSupportedLocale(currentSettings.locale));
	}, [currentSettings.locale]);

	return (
		<GlobalConfigContext.Provider
			value={{
				settings: currentSettings,
				saveSettings: handleSaveSettings,
			}}
		>
			{children}
		</GlobalConfigContext.Provider>
	);
}

GlobalProvider.propTypes = {
	settings: PropTypes.object,
	children: PropTypes.any.isRequired,
};

export const SettingsConsumer = GlobalConfigContext.Consumer;

export default GlobalConfigContext;
